import React, { useContext, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { ResultContext } from '../../contexts/ResultContext';
import { useTheme } from '../../contexts/ThemeContext';

const DuplicateSimilarityHistogram = () => {

    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    const { result } = useContext(ResultContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = result?.results?.visualization_data_jsons['3_duplicate_similarity_scores'];
        if (!url) return;

        const fetchData = async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error('Failed to fetch');
                const json = await response.json();
                const values = json.map(d => d.Similarity);
                setData(values);
            } catch (error) {
                console.error('Error loading histogram data:', error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [result]);

    if (loading || !Array.isArray(data) || data.length === 0) {
        return <div>Loading chart...</div>;
    }

    // Create histogram bins
    const binCount = 10;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const binSize = (max - min) / binCount;
    const bins = new Array(binCount).fill(0);

    data.forEach(score => {
        const index = Math.min(Math.floor((score - min) / binSize), binCount - 1);
        bins[index]++;
    });

    const xLabels = bins.map((_, i) =>
        `${(min + i * binSize).toFixed(2)} - ${(min + (i + 1) * binSize).toFixed(2)}`
    );

    const option = {
        title: {
            text: 'Distribution of Duplicate Similarity Scores',
            left: 'center',
            textStyle: { color: axisColor },
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: ({ 0: { name, value } }) => `${name}<br/>Count: ${value}`,
        },
        xAxis: {
            type: 'category',
            data: xLabels,
            name: 'Similarity',
            axisLabel: { rotate: 45, color: axisColor },
            nameTextStyle: {
                color: axisColor
            }
        },
        yAxis: {
            type: 'value',
            name: 'Count',
            axisLabel: { color: axisColor },
            nameTextStyle: {
                color: axisColor
            }
        },
        grid: {
            left: '2%',
            right: '10%',
            bottom: '15%',
            top: '15%',
            containLabel: true,
        },
        series: [
            {
                type: 'bar',
                data: bins,
                itemStyle: {
                    color: '#6366f1',
                },
            },
        ],
    };

    return (
        <div className="border-2 border-gray-300 rounded-lg p-4 shadow">
            <ReactECharts option={option} style={{ height: 400 }} />
        </div>
    );
};

export default DuplicateSimilarityHistogram;
